import { useTranslation } from "@opendash/i18n";
import { Icon } from "@opendash/icons";
import { Button, Flex, Form, Select } from "antd";
import { Rule } from "antd/es/form";
import React from "react";
import { CSSProperties } from "styled-components";
import { useOperationOptions } from "../hooks/useOperationOptions";
import { FormValues, Operation } from "../types/FormValues";
import ArrayValueInput from "./value/ArrayValueInput";
import BooleanValueInput from "./value/BooleanValueInput";
import DateValueInput from "./value/DateValueInput";
import DefaultValueInput from "./value/DefaultValueInput";
import NumberValueInput from "./value/NumberValueInput";
import PointerValueInput from "./value/PointerValueInput";
import RelationValueInput from "./value/RelationValueInput";

type Props = {
  index: number;
  onDelete: () => void;
};
const OperationItem = (props: Props) => {
  const { fieldOptions, operationOptions, currentOperation } =
    useOperationOptions(props.index);
  const form = Form.useFormInstance<FormValues>();
  const t = useTranslation();

  // handle field change and reset operator and value if the field type changes
  function handleFieldChange(value: string) {
    const selectedField = fieldOptions.find((field) => field.value === value);
    console.log({ selectedField });
    if (
      currentOperation &&
      currentOperation.field.type !== selectedField?.type
    ) {
      form.setFieldValue(["operations", props.index, "operator"], undefined);
      form.setFieldValue(["operations", props.index, "value"], undefined);
    }
    form.setFieldValue(["operations", props.index, "field"], selectedField);
  }

  const styles: CSSProperties = { flex: 1, flexBasis: "50%", marginBottom: 0 };

  const rule: Rule = {
    required: true,
    message: t("openware:parse.form.operation.error"),
  };

  const valueProps = {
    rules: [rule],
    index: props.index,
    styles,
    label: t("openware:parse.form.operation.value.label"),
  };

  return (
    <Flex gap={8} align="flex-end">
      <Form.Item
        label={t("openware:parse.form.operation.field.label")}
        name={[props.index, "field"]}
        rules={[rule]}
        style={styles}
      >
        <Select
          options={fieldOptions}
          onChange={(value) => handleFieldChange(value)}
          value={form.getFieldValue(["operations", props.index, "field"])}
        />
      </Form.Item>
      <Form.Item
        label={t("openware:parse.form.operation.operator.label")}
        name={[props.index, "operator"]}
        rules={[rule]}
        style={styles}
      >
        <Select options={operationOptions} />
      </Form.Item>
      {getValueComponentByOperation(currentOperation, valueProps)}
      <Button onClick={props.onDelete} danger>
        <Icon icon="fa:trash" />
      </Button>
    </Flex>
  );
};

function getValueComponentByOperation(operation: Operation, props?: any) {
  const isRelationOperator =
    operation?.operator === "doesNotMatchKeyInQuery" ||
    operation?.operator === "matchesKeyInQuery" ||
    operation?.operator === "matchesQuery" ||
    operation?.operator === "doesNotMatchQuery";

  const isExistsOperator =
    operation?.operator === "exists" || operation?.operator === "doesNotExist";

  if (
    operation?.field?.type === "array" ||
    operation?.field?.type === "file" ||
    operation?.field?.type === "object" ||
    isExistsOperator
  )
    return <></>;

  if (
    operation?.operator === "containedIn" ||
    operation?.operator === "notContainedIn"
  ) {
    return <ArrayValueInput {...props} type={operation.field.type} />;
  }

  if (operation?.field?.type === "relation" || isRelationOperator)
    return <RelationValueInput {...props} />;
  if (operation?.field?.type === "number")
    return <NumberValueInput {...props} />;
  if (operation?.field?.type === "boolean")
    return <BooleanValueInput {...props} />;
  if (operation?.field?.type === "date") return <DateValueInput {...props} />;
  if (operation?.field?.type === "pointer")
    return (
      <PointerValueInput {...props} targetClass={operation.field.targetClass} />
    );
  return <DefaultValueInput {...props} />;
}

export default OperationItem;
